import { useCommandActionsContext } from '@festina-life/authentication'
import type { Optional } from '@festina-life/core'
import { Button, IconButton } from '@festina-life/ui/actions'
import { Tooltip } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'
import type { CommandActionVariant } from '@festina-life/ui/theme'
import { type ComponentProps, type JSX, useMemo } from 'react'

import type { EventVersionWithCommandType } from '../../hooks/useFilterCommands'
import { useSettingsContext } from '../settingsProvider/settingsContext'
import { useActionsContext } from './actionsContext'

type Props = {
  buttonVariant?: 'primary' | 'secondary' | 'discreet'
  registrationId: string
  commandActionVariant: CommandActionVariant
  isActionAvailable: boolean
  buttonText: string | JSX.Element
  eventVersionWithCommand?: Optional<EventVersionWithCommandType>
  isFutureCommand?: boolean
}

export const CommandModalButtonActions = ({
  buttonVariant,
  registrationId,
  commandActionVariant,
  isActionAvailable,
  buttonText,
  eventVersionWithCommand,
  isFutureCommand,
}: Props) => {
  const translate = useTranslate()

  const { onOpen } = useActionsContext()
  const { shouldDisableMutationActions } = useCommandActionsContext()

  const {
    authState: { accounts },
  } = useSettingsContext()
  const { localAccountId } = accounts[0]

  const { commandVersion } = eventVersionWithCommand ?? {}

  const { commandPendingProcessingVersion } = commandVersion ?? {}
  const { cancelPendingUserId, cancelApprovalPending, approvalPending } =
    commandPendingProcessingVersion ?? {}

  const requiresApproval = !!approvalPending
  const hasBeenCancelledOrRolledback =
    !!commandVersion?.cancelledTime || !!eventVersionWithCommand?.rolledBackTime

  const isApproveVariant =
    commandActionVariant === 'approve' ||
    commandActionVariant === 'approvePendingCancellation'

  // You are not allowed to approve your own commands
  const canLoggedInUserApprove =
    commandActionVariant === 'approve'
      ? commandVersion?.createdBy !== localAccountId
      : cancelPendingUserId !== localAccountId

  const isApprovalAllowed =
    isActionAvailable && canLoggedInUserApprove && isApproveVariant

  const tooltipMessage = useMemo(() => {
    if (isApproveVariant && !canLoggedInUserApprove) {
      return translate('commandData.notAllowedToApproveOwnCommands')
    }

    if (!isActionAvailable) {
      // Previously roles that were missing were shown. But due to higher management, and an Deloitte article (that I cannot find)
      // it was decided that we should not show these roles anymore.
      return translate('actionReasonCode.insufficientAccess')
    }

    if (commandActionVariant === 'cancel' && cancelApprovalPending) {
      return translate(
        'actionReasonCode.approvePendingCancellationAlreadyPending',
      )
    }

    return undefined
  }, [
    isApproveVariant,
    canLoggedInUserApprove,
    isActionAvailable,
    commandActionVariant,
    cancelApprovalPending,
    translate,
  ])

  const isReadVariant = commandActionVariant === 'read'
  const hasApprovalAccessIfNeeded = !isApproveVariant || isApprovalAllowed
  const isCancelVariant = commandActionVariant === 'cancel'
  const hasNoCancelApprovalPending = !cancelApprovalPending

  const defaultProps: Pick<
    ComponentProps<typeof IconButton>,
    'iconSize' | 'onClick' | 'tip'
  > = {
    iconSize: 'md',
    onClick: (e) => {
      e.stopPropagation()
      onOpen(registrationId)
    },
    ...(shouldDisableMutationActions && commandActionVariant !== 'read'
      ? { tip: translate('commandData.onlyScenarioCommandAllowed') }
      : { tip: null }),
  }

  // Determine when to show button without tooltip wrapper
  const shouldShowButtonWithoutTooltip =
    isReadVariant ||
    (isActionAvailable &&
      hasApprovalAccessIfNeeded &&
      isCancelVariant &&
      hasNoCancelApprovalPending)

  const ButtonVariant = {
    create: (
      <Button
        variant={buttonVariant}
        onClick={() => onOpen(registrationId)}
        disabled={!isActionAvailable}
      >
        {buttonText}
      </Button>
    ),
    read: <IconButton iconName="BookOpen" {...defaultProps} />,
    update: requiresApproval ? null : (
      <IconButton
        disabled={!isActionAvailable || !!shouldDisableMutationActions}
        iconName="ArrowPathRoundedSquare"
        {...defaultProps}
      />
    ),
    approve: requiresApproval ? (
      <IconButton
        disabled={!isApprovalAllowed || !!shouldDisableMutationActions}
        iconName="ApproveAdd"
        {...defaultProps}
      />
    ) : null,
    approvePendingCancellation: cancelApprovalPending ? (
      <IconButton
        disabled={!isApprovalAllowed || !!shouldDisableMutationActions}
        iconName="ApproveRemove"
        {...defaultProps}
      />
    ) : null,
    cancel:
      !hasBeenCancelledOrRolledback && !cancelApprovalPending ? (
        <IconButton
          disabled={
            !isActionAvailable ||
            (!!cancelApprovalPending && isFutureCommand) ||
            !!shouldDisableMutationActions
          }
          iconName="Trash"
          {...defaultProps}
        />
      ) : null,
    declinePendingCancellation: cancelApprovalPending ? (
      <IconButton
        disabled={!isActionAvailable || !!shouldDisableMutationActions}
        iconName="Trash"
        {...defaultProps}
      />
    ) : null,
  }[commandActionVariant]

  return shouldShowButtonWithoutTooltip ? (
    ButtonVariant
  ) : (
    <Tooltip tip={tooltipMessage} placement="top">
      {ButtonVariant}
    </Tooltip>
  )
}
